import { Injectable, Injector, computed, effect, inject, signal, untracked } from '@angular/core';
import { Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';
import { ExtranetService } from './extranet.service';
import { IdentityService } from './identity.service';
import { GroupContextService } from './group-context.service';
import { PropertiesService } from './properties.service';
import { ShellBannerService } from './shell-banner.service';
import { ViewAsGroup } from '../models/admin-tenant.model';

/**
 * Admin "Open as this group" (F7, handoff §17, WI #3328; write mode F9, story #3362, revised by
 * the PO on 2026-09-19): the viewpoint override a platform admin pins on a tenant, read by
 * `viewAsInterceptor` (which sends `X-Extranet-View-As-Group` on every request that reaches the
 * extranet BE) and mirrored by the shell banner.
 *
 * The override is per tab (`sessionStorage 'ext.viewAsGroup'`), and the signal is seeded from it
 * synchronously so the very first `GET /extranet/access/me` of a reload already carries the
 * header — the identity, the nav and the guards then act as the viewed group's manager from the
 * first paint, never as the admin for a moment.
 *
 * Authorisation is the TOKEN's, not a header's: the BE verifies the bearer token itself and, when
 * its email is in the platform_admin allowlist, runs ANY request under the group header — read or
 * write — exactly as that group's own manager would, attributed to the admin's email and audited
 * (method, path, outcome, body of an admitted write, response status). The BE knows no "mode".
 *
 * The read-only default is a BROWSER-SIDE SAFEGUARD: a viewpoint always STARTS read-only, and
 * while `writeMode` is off the interceptor refuses every non-read request in the browser (it is
 * never sent, so it leaves no audit row — by design). The admin switches on an explicit, per-tab
 * WRITE MODE from the banner, after a confirmation; while it is on, writes go out with just the
 * group header and the BE runs them for real. Write mode is persisted next to the pin
 * (`sessionStorage 'ext.viewAsMode'`) so a reload keeps it, and it can never outlive the pin:
 * `clear()` / `exit()` / `switchRole` / a new `enter()` all drop it.
 *
 * - `enter(group)` pins the group read-only, re-resolves the identity (the BE answers with the
 *   viewed group's role/name) and lands on `/extranet/dashboard`, whose dispatcher picks the
 *   agency home or the hotel dashboard from that role (AC-17.2, AC-17.4).
 * - `requestWrite()` asks for the confirmation (the shell renders the dialog while
 *   `writeConfirmPending` is true); `confirmWrite()` / `cancelWrite()` answer it. `enableWrite()`
 *   / `disableWrite()` flip the mode itself — disabling needs no confirmation.
 * - `exit()` clears everything, re-resolves as the admin and returns to the groups list (AC-17.5).
 * - `clear()` only forgets the pin (and the mode): `IdentityService.switchRole` calls it before
 *   its own reload, so a manual role change can never leave a stale "viewing as" claim behind
 *   (AC-17.6); the interceptor calls it too when the BE will never honour the pin (group gone,
 *   caller not an admin), then retries the read as the operator themselves instead of trapping
 *   them.
 *
 * The services it drives (identity, group context, properties, banner) are resolved lazily
 * through the injector rather than injected up front: `IdentityService` injects this service for
 * `switchRole`, and an eager back-reference would be a construction cycle.
 */
@Injectable({ providedIn: 'root' })
export class ViewAsService {
  static readonly STORAGE_KEY = 'ext.viewAsGroup';
  /** Per-tab write mode; holds `MODE_STORAGE_WRITE` while editing is on, absent otherwise. A
   *  browser-side marker only — it is never sent to the BE. */
  static readonly MODE_STORAGE_KEY = 'ext.viewAsMode';
  static readonly MODE_STORAGE_WRITE = 'write';
  /** The only view-as header. Honoured by the BE only for a verified platform-admin token, for
   *  reads and writes alike. */
  static readonly HEADER = 'X-Extranet-View-As-Group';

  private readonly injector = inject(Injector);
  private readonly router = inject(Router);
  private readonly translate = inject(TranslateService);
  private readonly shellBanner = inject(ShellBannerService);
  private readonly snackBar = inject(MatSnackBar);

  /** The pinned group, or null when the admin is themselves. Seeded from sessionStorage. */
  readonly group = signal<ViewAsGroup | null>(ViewAsService.readStored());
  readonly active = computed(() => this.group() !== null);
  /** True while the interceptor lets writes leave the browser as the viewed group. Seeded from
   *  sessionStorage, only ever true together with a pin. */
  readonly writeMode = signal<boolean>(ViewAsService.readStoredMode());
  /** True between "Enable editing" and the confirmation's answer — the shell shows the dialog. */
  readonly writeConfirmPending = signal(false);

  /** Bumped when the UI language (or its translations) arrives, so the banner re-renders. */
  private readonly langTick = signal(0);

  constructor() {
    this.translate.onLangChange.subscribe(() => this.langTick.update(n => n + 1));
    this.translate.onDefaultLangChange.subscribe(() => this.langTick.update(n => n + 1));
    // The banner follows the pin, the mode and the identity the pin produced: shown once GET /me
    // has answered as the viewed group (its role names the banner), with the group's own currency
    // once the group context has it, cleared the moment the pin goes. Read-only is the amber
    // "viewing" bar; write mode is the red "editing" bar — tone, icon, wording and the toggle's
    // label all change together, so colour is never the only carrier of the mode.
    effect(() => {
      const group = this.group();
      const writing = this.writeMode();
      this.langTick();
      if (!group) { untracked(() => this.shellBanner.clear()); return; }
      const identity = this.injector.get(IdentityService);
      const groupCtx = this.injector.get(GroupContextService);
      const me = identity.me();
      const currency = groupCtx.group()?.currency ?? null;
      untracked(() => {
        groupCtx.ensureLoaded();
        const role = me?.role ?? null;
        const roleLabel = role === 'HOTEL_MANAGER' ? this.translate.instant('topbar.viewAs.banner.hotelManager')
          : role === 'AGENCY_MANAGER' ? this.translate.instant('topbar.viewAs.banner.agencyManager') : '';
        const parts = [me?.viewAsGroupName || group.name, roleLabel, currency, writing ? null : this.translate.instant('topbar.viewAs.banner.readOnly')]
          .filter((p): p is string => !!p);
        const details = parts.join(' · ');
        this.shellBanner.show(writing
          ? {
            tone: 'red',
            icon: 'edit',
            text: this.translate.instant('topbar.viewAs.banner.writeText', { details }),
            actionLabel: this.translate.instant('topbar.viewAs.banner.backToAdmin'),
            action: () => this.exit(),
            secondaryAction: {
              label: this.translate.instant('topbar.viewAs.banner.disableEditing'),
              icon: 'edit_off',
              tone: 'outline',
              handler: () => this.disableWrite(),
            },
          }
          : {
            tone: 'amber',
            icon: 'visibility',
            text: this.translate.instant('topbar.viewAs.banner.text', { details }),
            actionLabel: this.translate.instant('topbar.viewAs.banner.backToAdmin'),
            action: () => this.exit(),
            secondaryAction: {
              label: this.translate.instant('topbar.viewAs.banner.enableEditing'),
              icon: 'edit',
              tone: 'outline',
              handler: () => this.requestWrite(),
            },
          });
      });
    });
  }

  /** Pin `group` read-only, re-resolve the identity as its manager and land on that role's home. */
  enter(group: ViewAsGroup): void {
    this.forgetMode();
    ViewAsService.persist(group);
    this.group.set(group);
    this.reloadIdentity('/extranet/dashboard');
  }

  /** Drop the pin (and the mode), re-resolve as the admin and return to the groups list. No-op when not viewing. */
  exit(): void {
    if (!this.clear()) return;
    // The admin's own entitlement (the BE reports `availableRoles: ["ADMIN"]` under view-as) is
    // what "Back to admin" lands on — make the dev switcher's pick agree, or a stale
    // `ext.activeRole` from before the pin would answer as another role and bounce the admin.
    const identity = this.injector.get(IdentityService);
    if (identity.availableRoles().includes('ADMIN')) {
      try { sessionStorage.setItem(ExtranetService.ACTIVE_ROLE_KEY, 'ADMIN'); } catch { /* private mode */ }
    }
    this.reloadIdentity('/extranet/admin');
  }

  /** Forget the pin and the mode without reloading or navigating; returns whether a pin was active. */
  clear(): boolean {
    const wasActive = this.active();
    this.forgetMode();
    try { sessionStorage.removeItem(ViewAsService.STORAGE_KEY); } catch { /* private mode */ }
    this.group.set(null);
    return wasActive;
  }

  /** "Enable editing" from the banner: ask first. No-op when nothing is pinned or already editing. */
  requestWrite(): void {
    if (!this.active() || this.writeMode()) return;
    this.writeConfirmPending.set(true);
  }

  /** The confirmation's primary CTA: switch on, then say so once (the banner turning red is
   *  never the only signal — its text and the toggle's label change too). */
  confirmWrite(): void {
    this.writeConfirmPending.set(false);
    if (!this.active()) return;
    this.enableWrite();
    this.snackBar.open(this.translate.instant('topbar.viewAs.banner.writeMode'), undefined, { duration: 4000 });
  }

  /** The confirmation's Cancel / Escape / backdrop. */
  cancelWrite(): void {
    this.writeConfirmPending.set(false);
  }

  /** Switch write mode on for the pinned group (per tab, survives a reload). No-op without a pin. */
  enableWrite(): void {
    if (!this.active()) return;
    try { sessionStorage.setItem(ViewAsService.MODE_STORAGE_KEY, ViewAsService.MODE_STORAGE_WRITE); } catch { /* private mode */ }
    this.writeMode.set(true);
  }

  /** Back to read-only. Needs no confirmation: the safe direction. */
  disableWrite(): void {
    this.forgetMode();
  }

  private forgetMode(): void {
    try { sessionStorage.removeItem(ViewAsService.MODE_STORAGE_KEY); } catch { /* private mode */ }
    this.writeMode.set(false);
    this.writeConfirmPending.set(false);
  }

  /** Re-resolve GET /me (now with or without the header), refresh the property list the topbar
   *  selector reads (a hotel viewpoint has its own properties), then go to `landing`. */
  private reloadIdentity(landing: string): void {
    const identity = this.injector.get(IdentityService);
    identity.load(() => {
      this.injector.get(PropertiesService).load();
      this.router.navigateByUrl(landing);
    });
  }

  private static readStored(): ViewAsGroup | null {
    try {
      const raw = sessionStorage.getItem(ViewAsService.STORAGE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw) as Partial<ViewAsGroup>;
      return typeof parsed?.id === 'string' && parsed.id ? { id: parsed.id, name: typeof parsed.name === 'string' ? parsed.name : '' } : null;
    } catch {
      return null;
    }
  }

  /** Write mode is only meaningful next to a pin: a stray mode key without one is ignored. */
  private static readStoredMode(): boolean {
    try {
      return ViewAsService.readStored() !== null && sessionStorage.getItem(ViewAsService.MODE_STORAGE_KEY) === ViewAsService.MODE_STORAGE_WRITE;
    } catch {
      return false;
    }
  }

  private static persist(group: ViewAsGroup): void {
    try { sessionStorage.setItem(ViewAsService.STORAGE_KEY, JSON.stringify({ id: group.id, name: group.name })); } catch { /* private mode */ }
  }
}
